import { useId } from "react";
import { cn } from "@/lib/cn";

/**
 * Sparkline DECORATIVO (sin datos reales) · la curva de cada card se compone de
 * VARIAS funciones seno sumadas (distinta frecuencia, amplitud y fase) más una
 * tendencia lineal, lo que da una onda más orgánica que un solo arco. `shape`
 * elige la combinación; `color` el trazo/relleno. `aria-hidden` porque no
 * comunica un dato.
 */
type Componente = { f: number; a: number; p: number };
const FORMAS: Record<string, { comps: Componente[]; tendencia: number }> = {
  // "a" · tendencia ascendente con ondulación fina encima.
  a: { comps: [{ f: 1, a: 0.5, p: 0.4 }, { f: 2.6, a: 0.2, p: 1.1 }, { f: 5.2, a: 0.09, p: 2.3 }], tendencia: 0.7 },
  // "b" · campana (sube y baja) con textura.
  b: { comps: [{ f: 1, a: 0.55, p: 2.2 }, { f: 2.1, a: 0.18, p: 0.5 }, { f: 4.4, a: 0.1, p: 1.7 }], tendencia: 0 },
  // "c" · ondas encadenadas con leve descenso.
  c: { comps: [{ f: 1, a: 0.48, p: 1.1 }, { f: 3.1, a: 0.16, p: 0.2 }, { f: 6, a: 0.08, p: 2.9 }], tendencia: -0.45 },
  // "d" · descendente con rebote.
  d: { comps: [{ f: 1.3, a: 0.42, p: 0.8 }, { f: 2.8, a: 0.22, p: 2.0 }, { f: 5.5, a: 0.11, p: 0.3 }], tendencia: -0.6 },
  // "flat" · ondulación mínima y calmada (varias funciones, baja amplitud).
  flat: { comps: [{ f: 1.6, a: 0.14, p: 0.6 }, { f: 3.6, a: 0.07, p: 1.9 }], tendencia: 0 },
};

/** Curva suave (Catmull-Rom → Bézier) que pasa por `pts`. */
function smoothPath(pts: [number, number][]): string {
  let d = `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return d;
}

/** Muestrea la suma de funciones y la normaliza al alto del viewBox (centrada). */
function construirCurva(shape: string, W: number, H: number): string {
  const { comps, tendencia } = FORMAS[shape] ?? FORMAS.flat;
  const N = 40;
  const crudo: number[] = [];
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    let y = 0;
    for (const c of comps) y += c.a * Math.sin(c.f * t * Math.PI * 2 + c.p);
    y += tendencia * (t - 0.5);
    crudo.push(y);
  }
  const min = Math.min(...crudo);
  const max = Math.max(...crudo);
  const rango = max - min || 1;
  const padY = H * 0.22; // margen arriba/abajo · centra la curva en el card
  const pts: [number, number][] = crudo.map((v, i) => [
    (i / N) * W,
    padY + (1 - (v - min) / rango) * (H - padY * 2),
  ]);
  return smoothPath(pts);
}

export function Sparkline({
  shape = "flat",
  color = "var(--color-slate-400)",
  className,
}: {
  shape?: "a" | "b" | "c" | "d" | "flat";
  color?: string;
  className?: string;
}) {
  const gradId = useId();
  const W = 120;
  const H = 48;
  const line = construirCurva(shape, W, H);
  const area = `${line} L ${W},${H} L 0,${H} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={cn("overflow-visible", className)}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
